import React, { useMemo, useState, useEffect, useRef } from "react";
import { useIntl } from "@umijs/max";
import { theme } from "antd";

/**
 * 世界地图数据项接口
 */
interface WorldMapChartData {
  /** 匹配标识（对应 GeoJSON properties 中的字段，如 name 或 postal） */
  key: string;
  /** 用于排序和颜色深浅的数值 */
  value: number;
  /** 国家显示名称 */
  name?: string;
  /** Tooltip 中可展示的额外字段 */
  [key: string]: unknown;
}

interface GeoPoint {
  lon: number;
  lat: number;
}

interface GeoPolygon {
  id: string;
  id_iso2?: string;
  name: string;
  group: string;
  points: GeoPoint[];
}

/**
 * WorldMapChart 组件属性接口
 */
interface WorldMapChartProps {
  /** 地图数据数组 */
  data: WorldMapChartData[];
  /** 匹配 GeoJSON 属性的字段名，默认 'id_iso2' */
  joinBy?: string;
  /** 地图宽度，默认 800 */
  width?: number;
  /** 地图高度，默认 400 */
  height?: number;
  /** 从浅到深的渐变色数组 */
  colors?: string[];
  /** 无数据国家填充色，默认 '#f5f5f5' */
  noDataColor?: string;
  /** 自定义 Tooltip 渲染函数 */
  tooltip?: (data: WorldMapChartData) => React.ReactNode;
  /** 悬浮到有数据国家时触发 */
  onRegionHover?: (data: WorldMapChartData | null) => void;
  /** 空数据提示文本 */
  emptyText?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 自定义类名 */
  className?: string;
}

/** 默认渐变色板（由深到浅） */
const DEFAULT_COLORS = [
  "#0050B3", // 第 1 名
  "#1E64BD",
  "#2E6FC3",
  "#3D7AC8",
  "#4C85CE",
  "#5C90D3",
  "#6B9BD9",
  "#7AA6DE",
  "#8AB1E4",
  "#99BCE9", // 第 10 名
];

/** 默认无数据颜色 */
const DEFAULT_NO_DATA_COLOR = "#99BCE9";

/** 默认尺寸 */
const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 400;

/** 世界地图 GeoJSON 数据源 */
const WORLD_GEOJSON_URL = "/geo.json";

const normalizeCountryKey = (value: unknown) => {
  return String(value ?? "")
    .trim()
    .toUpperCase();
};

/**
 * 世界地图数据可视化组件
 */
const WorldMapChart: React.FC<WorldMapChartProps> = ({
  data = [],
  joinBy = "id_iso2",
  width,
  height = DEFAULT_HEIGHT,
  colors = DEFAULT_COLORS,
  noDataColor = DEFAULT_NO_DATA_COLOR,
  tooltip,
  onRegionHover,
  emptyText,
  style,
  className,
}) => {
  const intl = useIntl();
  const { token } = theme.useToken();
  const containerRef = useRef<HTMLDivElement>(null);
  const [geoData, setGeoData] = useState<GeoPolygon[]>([]);
  const [loading, setLoading] = useState(true);
  const [containerWidth, setContainerWidth] = useState<number>(
    width || DEFAULT_WIDTH,
  );
  const [hoveredOverlayKey, setHoveredOverlayKey] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateWidth = () => {
      const nextWidth = el.clientWidth || width || DEFAULT_WIDTH;
      setContainerWidth(nextWidth);
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [width]);

  const renderWidth = width || containerWidth || DEFAULT_WIDTH;

  useEffect(() => {
    setLoading(true);
    fetch(WORLD_GEOJSON_URL, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const polygons: GeoPolygon[] = [];
        (json.features || []).forEach((f: any, fIndex: number) => {
          const { geometry, properties, id } = f;
          if (!geometry) return;
          const featureId = String(
            id ||
              properties?.id ||
              properties?.postal ||
              properties?.ISO_A3 ||
              "",
          );
          const featureName = String(
            properties?.name || featureId || "Unknown",
          );

          const processCoords = (coords: any[][], polyIndex: number) => {
            const ring = coords[0];
            if (!ring || ring.length === 0) return;
            polygons.push({
              id: featureId,
              id_iso2: String(f.id_iso2 || "").toUpperCase(),
              name: featureName,
              group: `${fIndex}-${polyIndex}`,
              points: ring.map((c: any) => ({
                lon: Number(c[0]),
                lat: Number(c[1]),
              })),
            });
          };

          if (geometry.type === "Polygon") {
            processCoords(geometry.coordinates, 0);
          } else if (geometry.type === "MultiPolygon") {
            geometry.coordinates.forEach(
              (polyCoords: any[][], polyIndex: number) => {
                processCoords(polyCoords, polyIndex);
              },
            );
          }
        });

        console.log(
          "GeoJSON loaded and processed successfully:",
          polygons.length,
          "polygons from",
          (json.features || []).length,
          "features",
        );
        setGeoData(polygons);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load map data:", err);
        setLoading(false);
      });
  }, []);

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const normalized = data
      .map((item) => ({
        ...item,
        key: normalizeCountryKey(item.key),
        value: Number(item.value || 0),
      }))
      .filter((item) => item.key && Number.isFinite(item.value));

    const mergedMap = new Map<string, WorldMapChartData>();
    normalized.forEach((item) => {
      const prev = mergedMap.get(item.key);
      if (!prev) {
        mergedMap.set(item.key, item);
        return;
      }
      // 同国家码合并，避免分页/重复数据导致着色异常
      mergedMap.set(item.key, {
        ...item,
        value: Number(prev.value || 0) + item.value,
      });
    });

    const sortedData = [...mergedMap.values()].sort(
      (a, b) => b.value - a.value,
    );

    return sortedData.map((item, index) => {
      // 排名从 0 开始，index 为 0 表示第 1 名
      // 如果排名在前 10 (index 0-9)，则取对应颜色
      // 否则使用默认无数据颜色
      const _color = index < colors.length ? colors[index] : noDataColor;

      return {
        ...item,
        _color,
      };
    });
  }, [data, colors]);

  const colorMap = useMemo(() => {
    const map = new Map<string, WorldMapChartData & { _color: string }>();
    processedData.forEach((item) => {
      map.set(
        normalizeCountryKey(item.key),
        item as WorldMapChartData & { _color: string },
      );
    });
    return map;
  }, [processedData]);

  const normalizedGeoData = useMemo(() => {
    if (!geoData.length) return [];
    return geoData.map((p) => {
      return {
        ...p,
        joinKey: normalizeCountryKey((p as any)[joinBy] || p.id),
        fallbackKey: normalizeCountryKey(p.id),
      };
    });
  }, [geoData, joinBy]);

  const matchStats = useMemo(() => {
    if (!normalizedGeoData.length) return { total: 0, hit: 0 };
    let hit = 0;
    normalizedGeoData.forEach((poly) => {
      if (colorMap.get(poly.joinKey) || colorMap.get(poly.fallbackKey)) {
        hit += 1;
      }
    });
    return { total: normalizedGeoData.length, hit };
  }, [normalizedGeoData, colorMap]);

  useEffect(() => {
    if (!matchStats.total) return;
    console.log(
      `[WorldMapChart] match ${matchStats.hit}/${matchStats.total}, joinBy=${joinBy}`,
    );
  }, [matchStats, joinBy]);

  const bounds = useMemo(() => {
    if (!normalizedGeoData.length) return null;
    let minLon = Infinity;
    let maxLon = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    normalizedGeoData.forEach((poly) => {
      poly.points.forEach((p) => {
        if (p.lon < minLon) minLon = p.lon;
        if (p.lon > maxLon) maxLon = p.lon;
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
      });
    });

    if (
      !Number.isFinite(minLon) ||
      !Number.isFinite(maxLon) ||
      !Number.isFinite(minLat) ||
      !Number.isFinite(maxLat)
    ) {
      return null;
    }
    return { minLon, maxLon, minLat, maxLat };
  }, [normalizedGeoData]);

  const projectPoint = (lon: number, lat: number) => {
    if (!bounds) return { x: 0, y: 0 };
    const padding = 8;
    const lonRange = Math.max(bounds.maxLon - bounds.minLon, 1);
    const latRange = Math.max(bounds.maxLat - bounds.minLat, 1);
    const innerWidth = Math.max(renderWidth - padding * 2, 1);
    const innerHeight = Math.max(height - padding * 2, 1);
    const scale = Math.min(innerWidth / lonRange, innerHeight / latRange);
    const contentWidth = lonRange * scale;
    const contentHeight = latRange * scale;
    const offsetX = (renderWidth - contentWidth) / 2;
    const offsetY = (height - contentHeight) / 2;

    return {
      x: offsetX + (lon - bounds.minLon) * scale,
      y: offsetY + (bounds.maxLat - lat) * scale,
    };
  };

  const basePaths = useMemo(() => {
    return normalizedGeoData.map((poly) => {
      const d = poly.points
        .map((p, idx) => {
          const pt = projectPoint(p.lon, p.lat);
          return `${idx === 0 ? "M" : "L"} ${pt.x} ${pt.y}`;
        })
        .join(" ");
      return { ...poly, d: `${d} Z` };
    });
  }, [normalizedGeoData, bounds, renderWidth, height]);

  if (loading) {
    return (
      <div
        className={className}
        style={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: token.colorFillQuaternary,
          borderRadius: "8px",
          ...style,
        }}
      >
        <span style={{ color: token.colorTextDescription, fontSize: "14px" }}>
          {emptyText || intl.formatMessage({ id: "component.worldMap.loading" })}
        </span>
      </div>
    );
  }

  if (!basePaths.length) {
    return (
      <div
        className={className}
        style={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: token.colorFillQuaternary,
          borderRadius: "8px",
          ...style,
        }}
      >
        <span style={{ color: token.colorTextDescription, fontSize: "14px" }}>
          {emptyText || intl.formatMessage({ id: "component.worldMap.noData" })}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        height,
        overflow: "hidden",
        ...style,
      }}
    >
      <svg
        style={{ display: "block", width: "100%" }}
        width="100%"
        height={height}
        viewBox={`0 0 ${renderWidth} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={() => {
          setHoveredOverlayKey(null);
          onRegionHover?.(null);
        }}
      >
        {basePaths.map((poly) => (
          <path
            key={`base-${poly.group}`}
            d={poly.d}
            fill={noDataColor || token.colorFillTertiary}
            stroke="#fff"
            strokeWidth={0.5}
          />
        ))}
        {basePaths.map((poly) => {
          const matched =
            colorMap.get(poly.joinKey) || colorMap.get(poly.fallbackKey);
          if (!matched) return null;
          const title = `${matched.name || poly.name}: ${matched.value?.toLocaleString?.() ?? "-"}`;
          return (
            <path
              key={`overlay-${poly.group}`}
              d={poly.d}
              fill={matched._color}
              stroke="#fff"
              strokeWidth={0.5}
              style={{
                cursor: "pointer",
                transformBox: "fill-box",
                transformOrigin: "center",
                transform:
                  hoveredOverlayKey === poly.group ? "scale(1.1)" : "scale(1)",
                transition: "transform 0.15s ease",
              }}
              onMouseEnter={() => {
                setHoveredOverlayKey(poly.group);
                onRegionHover?.(matched);
              }}
              onMouseLeave={() => {
                setHoveredOverlayKey(null);
                onRegionHover?.(null);
              }}
            >
              {/* <title>{title}</title> */}
            </path>
          );
        })}
      </svg>
    </div>
  );
};

export default WorldMapChart;

export type { WorldMapChartData, WorldMapChartProps };
